import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Send } from 'lucide-react';
import { useEffect, useState } from 'react';

import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import { standaloneFieldSx } from '../../common/fieldStyles';
import { formatPhone } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';

// Status da conexão WhatsApp do tenant (GET /whatsapp) — habilita o envio do PDF.
export function useWhatsappConnected(enabled = true) {
    const [connected, setConnected] = useState(false);
    useEffect(() => {
        if (!enabled) { setConnected(false); return undefined; }
        let active = true;
        httpClient.get('/whatsapp')
            .then((response) => { if (active) setConnected(response.data?.status === 'connected'); })
            .catch(() => { if (active) setConnected(false); });
        return () => { active = false; };
    }, [enabled]);
    return connected;
}

// Modal para conferir/editar o número e enviar o PDF do orçamento por WhatsApp
// (`GET /servicequotes/{id}/whatsapp/recipient` + `POST /servicequotes/{id}/whatsapp/send`).
// `onResult(message, severity)` devolve o feedback para o snackbar da tela.
// `onSent()` avisa a tela que o envio ocorreu (o status do orçamento muda no backend:
// PDF e link do portal -> "Aguardando Aprovação").
export default function QuoteWhatsappDialog({ open, quoteId, quoteNumber, onClose, onResult, onSent }) {
    const [mode, setMode] = useState('pdf');
    const [phone, setPhone] = useState('');
    const [loadingPhone, setLoadingPhone] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || !quoteId) return undefined;
        let active = true;
        setPhone('');
        setMode('pdf');
        setLoadingPhone(true);
        httpClient.get(`/servicequotes/${quoteId}/whatsapp/recipient`)
            .then((response) => { if (active) setPhone(formatPhone(response.data?.phone || '')); })
            .catch((error) => console.error('Erro ao buscar celular do cliente:', error)) // usuário ainda pode digitar o número
            .finally(() => { if (active) setLoadingPhone(false); });
        return () => { active = false; };
    }, [open, quoteId]);

    const handleSend = async () => {
        if (!quoteId) return;
        try {
            setSending(true);
            await httpClient.post(`/servicequotes/${quoteId}/whatsapp/send`, { phone, mode, base_url: window.location.origin });
            onClose();
            onResult(mode === 'link' ? 'Link de aprovação enviado por WhatsApp' : 'PDF do orçamento enviado por WhatsApp', 'success');
            if (onSent) onSent();
        } catch (error) {
            console.error('Erro ao enviar orçamento por WhatsApp:', error);
            const data = error.response?.data;
            onResult((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao enviar o orçamento por WhatsApp', 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !sending && onClose()} fullWidth maxWidth="xs">
            <DialogTitle>Enviar por WhatsApp</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
                    O orçamento nº {quoteNumber} será enviado ao número abaixo. Confira antes de enviar.
                </Typography>
                <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)} sx={{ mb: 2 }}>
                    <FormControlLabel value="pdf" control={<Radio size="small" />} disabled={sending}
                        label={<Typography sx={{ fontSize: 13 }}>Enviar PDF (status: Aguardando Aprovação)</Typography>} />
                    <FormControlLabel value="link" control={<Radio size="small" />} disabled={sending}
                        label={<Typography sx={{ fontSize: 13 }}>Enviar link do portal para aprovação (status: Aguardando Aprovação)</Typography>} />
                </RadioGroup>
                <TextField
                    label="Telefone (WhatsApp)"
                    helperText={loadingPhone ? 'Buscando celular do cliente...' : 'Celular do cliente (editável). DDD + número, ex.: (11) 99999-9999'}
                    fullWidth
                    autoFocus
                    sx={standaloneFieldSx}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={sending}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || !phone.trim()}
                    startIcon={sending ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <Send size={14} strokeWidth={2} />}
                >
                    Enviar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
